import React, { useState, useEffect, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FlaskConical,
  Send,
  Mail,
  Upload,
  CheckCircle,
  XCircle,
  RefreshCw,
  FileText,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const NCB_SAMPLE_EMAIL = {
  MessageID: `test-${Date.now()}`,
  From: "ncb@ncb.dk",
  To: "inbound@inbound-hub.local",
  Subject: "Frigivelses mail fra NCB - Type A - AERO015",
  TextBody: `Dear Customer,

The invoice for order GOPRO;DK;12345;AERO015 have been paid and delivery can take place.

Catalog: AERO015
Status: Approved
Date: ${new Date().toISOString()}

Best regards,
NCB`,
  HtmlBody: null,
  Attachments: [],
};

export default function TestToolsPage() {
  const [activeTab, setActiveTab] = useState("postmark");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mockLogs, setMockLogs] = useState([]);

  // Postmark test form
  const [postmarkData, setPostmarkData] = useState({
    MessageID: `test-${Date.now()}`,
    From: "sender@example.com",
    To: "inbound@example.com",
    Subject: "",
    TextBody: "",
    HtmlBody: "",
  });

  // Upload test form
  const [uploadData, setUploadData] = useState({
    target_system: "vinylflow",
    reference_type: "",
    reference_id: "",
    hint_type: "",
    file: null,
  });

  const fetchMockLogs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/webhooks/mock-logs?limit=10`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setMockLogs(data.logs);
      }
    } catch (error) {
      console.error("Failed to fetch mock logs:", error);
    }
  }, []);

  useEffect(() => {
    fetchMockLogs();
  }, [fetchMockLogs]);

  const handlePostmarkTest = async () => {
    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${API}/inbound/postmark`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Postmark-Token": "test-token",
        },
        body: JSON.stringify(postmarkData),
      });

      const data = await response.json();

      setResult({
        success: response.ok,
        status: response.status,
        data,
      });

      if (response.ok) {
        toast.success(`Email received! Item ID: ${data.item_id}`);
      } else {
        toast.error(`Failed: ${data.detail || "Unknown error"}`);
      }
    } catch (error) {
      setResult({
        success: false,
        error: error.message,
      });
      toast.error("Failed to send test email");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadTest = async () => {
    if (!uploadData.file) {
      toast.error("Please select a file");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Create upload session
      const sessionResponse = await fetch(`${API}/inbound/upload-sessions`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          target_system: uploadData.target_system,
          reference_type: uploadData.reference_type || null,
          reference_id: uploadData.reference_id || null,
          hint_type: uploadData.hint_type || null,
        }),
      });

      if (!sessionResponse.ok) {
        throw new Error("Failed to create upload session");
      }

      const sessionData = await sessionResponse.json();

      // Upload file
      const formData = new FormData();
      formData.append("file", uploadData.file);

      const uploadResponse = await fetch(
        `${API}/inbound/uploads/${sessionData.upload_session_id}`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      const uploadResult = await uploadResponse.json();

      setResult({
        success: uploadResponse.ok,
        status: uploadResponse.status,
        data: {
          session: sessionData,
          upload: uploadResult,
        },
      });

      if (uploadResponse.ok) {
        toast.success(`File uploaded! Item ID: ${uploadResult.item_id}`);
      } else {
        toast.error(`Failed: ${uploadResult.detail || "Unknown error"}`);
      }
    } catch (error) {
      setResult({
        success: false,
        error: error.message,
      });
      toast.error("Failed to upload file");
    } finally {
      setLoading(false);
    }
  };

  const loadNCBSample = () => {
    setPostmarkData({
      ...NCB_SAMPLE_EMAIL,
      MessageID: `test-${Date.now()}`,
    });
    toast.success("NCB sample loaded");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <Layout title="Test Tools">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="postmark" data-testid="tab-postmark">
            <Mail className="w-4 h-4 mr-2" />
            Postmark Webhook
          </TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-upload">
            <Upload className="w-4 h-4 mr-2" />
            File Upload
          </TabsTrigger>
          <TabsTrigger value="webhook-logs" data-testid="tab-webhook-logs">
            <FileText className="w-4 h-4 mr-2" />
            Mock Webhook Logs
          </TabsTrigger>
        </TabsList>

        {/* Postmark Webhook Test */}
        <TabsContent value="postmark">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="card-surface">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Postmark Email Payload
                </CardTitle>
                <CardDescription>
                  Simulate an inbound email from Postmark
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  variant="outline"
                  onClick={loadNCBSample}
                  className="w-full"
                  data-testid="load-ncb-sample-btn"
                >
                  <FlaskConical className="w-4 h-4 mr-2" />
                  Load NCB Type A Sample
                </Button>

                <div className="space-y-2">
                  <Label>Message ID</Label>
                  <Input
                    value={postmarkData.MessageID}
                    onChange={(e) =>
                      setPostmarkData((d) => ({ ...d, MessageID: e.target.value }))
                    }
                    placeholder="unique-message-id"
                    data-testid="postmark-message-id"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From</Label>
                    <Input
                      value={postmarkData.From}
                      onChange={(e) =>
                        setPostmarkData((d) => ({ ...d, From: e.target.value }))
                      }
                      placeholder="sender@example.com"
                      data-testid="postmark-from"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>To</Label>
                    <Input
                      value={postmarkData.To}
                      onChange={(e) =>
                        setPostmarkData((d) => ({ ...d, To: e.target.value }))
                      }
                      placeholder="inbound@example.com"
                      data-testid="postmark-to"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input
                    value={postmarkData.Subject}
                    onChange={(e) =>
                      setPostmarkData((d) => ({ ...d, Subject: e.target.value }))
                    }
                    placeholder="Email subject line"
                    data-testid="postmark-subject"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Text Body</Label>
                  <Textarea
                    value={postmarkData.TextBody}
                    onChange={(e) =>
                      setPostmarkData((d) => ({ ...d, TextBody: e.target.value }))
                    }
                    placeholder="Plain text email body"
                    rows={6}
                    className="font-mono text-sm"
                    data-testid="postmark-text-body"
                  />
                </div>

                <Button
                  onClick={handlePostmarkTest}
                  disabled={loading || !postmarkData.Subject}
                  className="w-full"
                  data-testid="send-postmark-btn"
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Test Email
                </Button>
              </CardContent>
            </Card>

            {/* Result Panel */}
            <Card className="card-surface">
              <CardHeader>
                <CardTitle className="text-base">Result</CardTitle>
              </CardHeader>
              <CardContent>
                {result ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      {result.success ? (
                        <Badge className="status-badge-success">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Success
                        </Badge>
                      ) : (
                        <Badge className="status-badge-error">
                          <XCircle className="w-3 h-3 mr-1" />
                          Failed
                        </Badge>
                      )}
                      {result.status && (
                        <Badge variant="outline">HTTP {result.status}</Badge>
                      )}
                    </div>

                    <pre className="json-viewer text-xs">
                      {JSON.stringify(result.data || result.error, null, 2)}
                    </pre>

                    {result.success && result.data?.item_id && (
                      <Button
                        variant="outline"
                        asChild
                        className="w-full"
                      >
                        <a href={`/items/${result.data.item_id}`}>
                          View Item →
                        </a>
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <FlaskConical className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Send a test to see results</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* File Upload Test */}
        <TabsContent value="upload">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="card-surface">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  File Upload Test
                </CardTitle>
                <CardDescription>
                  Test the upload session and file processing flow
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Target System</Label>
                  <Select
                    value={uploadData.target_system}
                    onValueChange={(value) =>
                      setUploadData((d) => ({ ...d, target_system: value }))
                    }
                  >
                    <SelectTrigger data-testid="upload-target-system">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vinylflow">VinylFlow</SelectItem>
                      <SelectItem value="localization">Localization</SelectItem>
                      <SelectItem value="distribution">Distribution</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Reference Type (optional)</Label>
                    <Input
                      value={uploadData.reference_type}
                      onChange={(e) =>
                        setUploadData((d) => ({ ...d, reference_type: e.target.value }))
                      }
                      placeholder="order"
                      data-testid="upload-reference-type"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Reference ID (optional)</Label>
                    <Input
                      value={uploadData.reference_id}
                      onChange={(e) =>
                        setUploadData((d) => ({ ...d, reference_id: e.target.value }))
                      }
                      placeholder="ORD-12345"
                      data-testid="upload-reference-id"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Hint Type (optional)</Label>
                  <Input
                    value={uploadData.hint_type}
                    onChange={(e) =>
                      setUploadData((d) => ({ ...d, hint_type: e.target.value }))
                    }
                    placeholder="ncb_clearance"
                    data-testid="upload-hint-type"
                  />
                </div>

                <div className="space-y-2">
                  <Label>File</Label>
                  <Input
                    type="file"
                    onChange={(e) =>
                      setUploadData((d) => ({
                        ...d,
                        file: e.target.files?.[0] || null,
                      }))
                    }
                    data-testid="upload-file-input"
                  />
                </div>

                <Button
                  onClick={handleUploadTest}
                  disabled={loading || !uploadData.file}
                  className="w-full"
                  data-testid="upload-file-btn"
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload File
                </Button>
              </CardContent>
            </Card>

            {/* Result Panel */}
            <Card className="card-surface">
              <CardHeader>
                <CardTitle className="text-base">Result</CardTitle>
              </CardHeader>
              <CardContent>
                {result ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      {result.success ? (
                        <Badge className="status-badge-success">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Success
                        </Badge>
                      ) : (
                        <Badge className="status-badge-error">
                          <XCircle className="w-3 h-3 mr-1" />
                          Failed
                        </Badge>
                      )}
                    </div>

                    <pre className="json-viewer text-xs">
                      {JSON.stringify(result.data || result.error, null, 2)}
                    </pre>

                    {result.success && result.data?.upload?.item_id && (
                      <Button
                        variant="outline"
                        asChild
                        className="w-full"
                      >
                        <a href={`/items/${result.data.upload.item_id}`}>
                          View Item →
                        </a>
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <FlaskConical className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Upload a file to see results</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Mock Webhook Logs */}
        <TabsContent value="webhook-logs">
          <Card className="card-surface">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Mock Webhook Receiver Logs</CardTitle>
                <CardDescription>
                  Events received by the built-in mock target
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchMockLogs}
                data-testid="refresh-mock-logs-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {mockLogs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No webhook events received yet</p>
                  <p className="text-sm mt-1">
                    Dispatch an item to see events here
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {mockLogs.map((log) => (
                    <div
                      key={log.id}
                      className="border rounded-md p-4 space-y-3"
                      data-testid={`mock-log-${log.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">
                            {log.payload?.event_type || "UNKNOWN"}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {formatDistanceToNow(new Date(log.received_at), {
                              addSuffix: true,
                            })}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(JSON.stringify(log.payload, null, 2))
                          }
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>

                      <pre className="json-viewer text-xs max-h-[200px] overflow-auto">
                        {JSON.stringify(log.payload, null, 2)}
                      </pre>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
